import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Save, Trash2, Tag as TagIcon } from 'lucide-react';
import { useTenant } from '../components/TenantContext.jsx';
import { tagsApi } from '../api/calls.js';
import { PageHeader, SectionHeader } from '../components/ui/PageHeader.jsx';
import { Card, Empty } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { Input } from '../components/ui/Input.jsx';

const PALETTE = [
    '#c84a2c', '#5e7a4e', '#c4892d', '#a4423b',
    '#3d362d', '#6b829e', '#8a5a83', '#5b7d8d',
];

const EMPTY = { name: '', color: '#c84a2c', description: '', displayOrder: 0, enabled: true };

export default function CallTagsPage() {
    const { t } = useTranslation('tags');
    const { hostId } = useTenant();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState(null);

    async function load() {
        if (!hostId) return;
        setLoading(true);
        try {
            const r = await tagsApi.list(hostId);
            setList(r.data || []);
            setError(null);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId]);

    async function save() {
        const draft = { ...editing };
        const tempId = draft.id || `__pending_${Date.now()}`;
        const prevList = list;
        const prevEditing = editing;
        // Optimistic：立刻關 editor + 本地更新列表
        setList((cur) => {
            if (draft.id) return cur.map((t) => (t.id === draft.id ? { ...t, ...draft } : t));
            return [{ ...draft, id: tempId, _pending: true }, ...cur];
        });
        setEditing(null);
        try {
            const r = await tagsApi.upsert({ hostId, ...draft });
            // server 回真實 doc → 取代 optimistic（拿真 firestore id）
            const real = r.data || r;
            setList((cur) => cur.map((t) => (t.id === tempId || t.id === draft.id ? real : t)));
        } catch (e) {
            setList(prevList);
            setEditing(prevEditing);
            setError(e);
        }
    }

    async function remove(id) {
        if (!confirm(t('dialog.deleteConfirm'))) return;
        const prevList = list;
        // Optimistic：本地立刻移除
        setList((cur) => cur.filter((t) => t.id !== id));
        try {
            await tagsApi.remove(id, hostId);
        } catch (e) {
            setList(prevList);
            setError(e);
        }
    }

    return (
        <div className="max-w-3xl">
            <PageHeader
                eyebrow={`tenant · ${hostId}`}
                title={t('page.title')}
                description={t('page.description')}
                actions={
                    <Button variant="accent" onClick={() => setEditing({ ...EMPTY })}>
                        <Plus size={14} /> {t('action.newTag')}
                    </Button>
                }
            />

            {error && (
                <Card className="bg-danger-100/40 border-danger-500/30 mb-4">
                    <div className="text-sm text-danger-500">{error.message}</div>
                </Card>
            )}

            {editing && (
                <TagEditor
                    tag={editing}
                    onChange={setEditing}
                    onSave={save}
                    onCancel={() => setEditing(null)}
                />
            )}

            {loading ? (
                <div className="text-ink-400 text-sm">{t('common:state.loading')}</div>
            ) : list.length === 0 ? (
                <Card>
                    <Empty
                        icon={TagIcon}
                        title={t('empty.title')}
                        description={t('empty.description')}
                        action={
                            <Button variant="accent" onClick={() => setEditing({ ...EMPTY })}>
                                <Plus size={14} /> {t('action.newTag')}
                            </Button>
                        }
                    />
                </Card>
            ) : (
                <div className="grid gap-2">
                    {list.map((tag) => (
                        <Card key={tag.id} className="flex items-center gap-4 hover:border-accent-300">
                            <span
                                className="size-8 rounded-md shrink-0"
                                style={{ background: tag.color || '#c84a2c' }}
                            />
                            <div className="flex-1 min-w-0">
                                <span className="font-medium text-ink-900">{tag.name}</span>
                                {tag.description && (
                                    <div className="text-xs text-ink-500 mt-0.5">{tag.description}</div>
                                )}
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => setEditing(tag)}>
                                {t('common:actions.edit')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => remove(tag.id)}>
                                <Trash2 size={12} className="text-danger-500" />
                            </Button>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

function TagEditor({ tag, onChange, onSave, onCancel }) {
    const { t } = useTranslation('tags');
    const isEdit = !!tag.id;
    return (
        <Card className="mb-6 ring-2 ring-accent-300">
            <SectionHeader title={isEdit ? t('editor.editTitle') : t('action.newTag')}>
                <div className="space-x-2">
                    <Button variant="ghost" onClick={onCancel}>
                        {t('common:actions.cancel')}
                    </Button>
                    <Button variant="accent" onClick={onSave}>
                        <Save size={14} /> {t('common:actions.save')}
                    </Button>
                </div>
            </SectionHeader>
            <div className="grid grid-cols-2 gap-4">
                <Input
                    label={t('field.name')}
                    value={tag.name}
                    onChange={(e) => onChange({ ...tag, name: e.target.value })}
                    placeholder={t('field.namePlaceholder')}
                />
                <Input
                    label={t('field.displayOrder')}
                    value={tag.displayOrder ?? 0}
                    onChange={(e) => onChange({ ...tag, displayOrder: Number(e.target.value) || 0 })}
                    type="number"
                    mono
                />
                <div className="col-span-2">
                    <span className="block text-xs font-medium text-ink-700 mb-1.5 uppercase tracking-wide">
                        {t('field.color')}
                    </span>
                    <div className="flex items-center gap-2">
                        {PALETTE.map((c) => (
                            <button
                                key={c}
                                onClick={() => onChange({ ...tag, color: c })}
                                className={[
                                    'size-7 rounded-md transition',
                                    tag.color === c
                                        ? 'ring-2 ring-offset-2 ring-ink-700 ring-offset-cream-50'
                                        : 'hover:scale-110',
                                ].join(' ')}
                                style={{ background: c }}
                            />
                        ))}
                        <input
                            type="color"
                            value={tag.color || '#c84a2c'}
                            onChange={(e) => onChange({ ...tag, color: e.target.value })}
                            className="size-7 rounded-md cursor-pointer"
                        />
                    </div>
                </div>
                <div className="col-span-2">
                    <Input
                        label={t('field.descriptionOptional')}
                        value={tag.description || ''}
                        onChange={(e) => onChange({ ...tag, description: e.target.value })}
                        placeholder={t('field.descriptionPlaceholder')}
                    />
                </div>
            </div>
        </Card>
    );
}
